import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserCog, Download, Megaphone, Trash2, Save } from "lucide-react";
import api, { apiErrorMessage, getToken, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// "waiting Xh" chip for the review queue — amber at 24h (nudge threshold), red at 72h
function slaChip(u) {
  const since = u.pending_since || u.created_at;
  if (!since) return null;
  const hrs = Math.max(0, (Date.now() - new Date(since).getTime()) / 36e5);
  const label = hrs >= 48 ? `${Math.floor(hrs / 24)}d ${Math.floor(hrs % 24)}h` : `${Math.floor(hrs)}h`;
  const cls = hrs >= 72 ? "border-red-500/60 text-red-400" : hrs >= 24 ? "border-amber-500/60 text-amber-400" : "border-white/[0.12] text-zinc-500";
  return { label, cls };
}

// repeated spam-guard blocks → the driver likely never sees our emails
function EmailBlockedBadge({ count, id }) {
  if ((count || 0) < 2) return null;
  return (
    <span
      data-testid={`email-blocked-badge-${id}`}
      title={`${count} emails blocked by the spam guard — this address may be unreachable. Follow up by phone.`}
      className="text-[10px] uppercase tracking-widest font-black border border-red-500/60 text-red-400 px-2 py-0.5 cursor-help"
    >
      Email blocked
    </span>
  );
}

// matches sms_service.to_e164 — US 10-digit numbers get +1
const normPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  const d11 = d.length === 10 ? "1" + d : d;
  return d11.length >= 11 && d11.length <= 15 ? `+${d11}` : null;
};

// display format: +15551234567 → (555) 123-4567; anything else returned as-is
const fmtPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  const d10 = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return d10.length === 10 ? `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}` : (p || "");
};

// fallback copy if the preview endpoint is unreachable — the endpoint's
// default_message is the source of truth and overwrites this on open
const BROADCAST_DEFAULT_MESSAGE =
  "Hi [Driver First Name], your Boat RV Moving driver profile needs to be completed. " +
  "Please log in and upload your DOT number, proof of insurance, and driver's license " +
  "to activate your account. Visit boatrvmoving.com/driver-signup to complete your onboarding.";

// single SMS = 160 chars; longer messages stitch at 153 chars/segment
const smsSegments = (len) => (len <= 160 ? 1 : Math.ceil(len / 153));

const REJECT_PRESETS = [
  "Insurance certificate is expired or doesn't show sufficient coverage.",
  "Driver's license photo is unreadable or expired.",
  "The DOT number's legal name doesn't match the applicant name.",
  "Application looks incomplete — some required information is missing.",
];

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(null); // user_id currently being role-switched
  const [audit, setAudit] = useState([]);
  const [optouts, setOptouts] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null); // pending driver being approved/rejected
  const [rejectTarget, setRejectTarget] = useState(null); // pending driver in the reject modal
  const [rejectReason, setRejectReason] = useState("");
  const [bcOpen, setBcOpen] = useState(false); // SMS broadcast compose panel
  const [bcMsg, setBcMsg] = useState(BROADCAST_DEFAULT_MESSAGE);
  const [bcPreview, setBcPreview] = useState(null); // per-audience recipient math from the server
  const [bcAudience, setBcAudience] = useState("incomplete"); // matches the default nudge copy
  const [bcSending, setBcSending] = useState(false);
  const [bcArmed, setBcArmed] = useState(false); // first click arms, second click sends
  const [bcResult, setBcResult] = useState(null); // summary after a send
  const [bcTestPhone, setBcTestPhone] = useState("");
  const [bcTestSending, setBcTestSending] = useState(false);
  const [bcTemplates, setBcTemplates] = useState([]); // saved message templates
  const [bcTemplateId, setBcTemplateId] = useState("default"); // "default" | template id | "" custom
  const [bcTplSaveOpen, setBcTplSaveOpen] = useState(false);
  const [bcTplName, setBcTplName] = useState("");
  const [bcTplSaving, setBcTplSaving] = useState(false);
  const [bcHistory, setBcHistory] = useState(null); // null = loading

  const bcStats = bcPreview?.audiences?.[bcAudience] || null;

  const openBroadcast = async () => {
    setBcOpen(true);
    setBcResult(null);
    setBcArmed(false);
    setBcMsg(BROADCAST_DEFAULT_MESSAGE);
    setBcTemplateId("default");
    setBcTplSaveOpen(false);
    setBcPreview(null);
    setBcHistory(null);
    api.get("/admin/sms-templates").then(({ data }) => setBcTemplates(data)).catch(() => {});
    api.get("/admin/sms-broadcast/history").then(({ data }) => setBcHistory(data)).catch(() => setBcHistory([]));
    try {
      const { data } = await api.get("/admin/sms-broadcast/preview");
      setBcPreview(data);
      if (data.default_message && bcTemplateId === "default") setBcMsg(data.default_message);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't load the recipient preview."));
    }
  };

  const pickTemplate = (id) => {
    setBcTemplateId(id);
    setBcArmed(false);
    setBcResult(null);
    if (id === "default") {
      setBcMsg(bcPreview?.default_message || BROADCAST_DEFAULT_MESSAGE);
    } else if (id) {
      const t = bcTemplates.find((x) => x.id === id);
      if (t) setBcMsg(t.body);
    }
  };

  const saveTemplate = async () => {
    if (bcTplSaving || !bcTplName.trim() || !bcMsg.trim()) return;
    setBcTplSaving(true);
    try {
      const { data } = await api.post("/admin/sms-templates", { name: bcTplName.trim(), body: bcMsg.trim() });
      setBcTemplates((list) => [...list, data]);
      setBcTemplateId(data.id);
      setBcTplSaveOpen(false);
      setBcTplName("");
      toast.success(`Template "${data.name}" saved.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't save the template."));
    } finally {
      setBcTplSaving(false);
    }
  };

  const deleteTemplate = async () => {
    if (!bcTemplateId || bcTemplateId === "default") return;
    try {
      await api.delete(`/admin/sms-templates/${bcTemplateId}`);
      setBcTemplates((list) => list.filter((t) => t.id !== bcTemplateId));
      setBcTemplateId("default");
      setBcMsg(bcPreview?.default_message || BROADCAST_DEFAULT_MESSAGE);
      toast.success("Template deleted.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete the template."));
    }
  };

  const sendBroadcast = async () => {
    if (bcSending || !bcMsg.trim()) return;
    if (!bcArmed) {
      setBcArmed(true); // explicit confirm click before a mass text goes out
      return;
    }
    setBcSending(true);
    try {
      const { data } = await api.post("/admin/sms-broadcast", { message: bcMsg.trim(), audience: bcAudience });
      setBcResult(data);
      toast.success(`Broadcast done — ${data.sent} sent, ${data.failed} failed, ${data.skipped} skipped. Logged in the Email/SMS log.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Broadcast failed."));
    } finally {
      setBcSending(false);
      setBcArmed(false);
    }
  };

  const sendBroadcastTest = async () => {
    if (bcTestSending || !bcTestPhone.trim() || !bcMsg.trim()) return;
    setBcTestSending(true);
    try {
      const { data } = await api.post("/admin/sms-broadcast", { message: bcMsg.trim(), test_phone: bcTestPhone.trim() });
      toast.success(
        data.sent === 1
          ? `Test text sent to ${bcTestPhone.trim()}.`
          : `Test attempted — check the Email/SMS log for the delivery status.`
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Test send failed."));
    } finally {
      setBcTestSending(false);
    }
  };

  const exportConsent = async () => {
    try {
      const { data } = await api.get("/admin/consent-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "consent-records.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Consent records downloaded.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const loadPending = async () => {
    try {
      const { data } = await api.get("/admin/drivers/pending");
      setPending(data);
    } catch {
      /* pending panel is non-fatal */
    } finally {
      setPendingLoading(false);
    }
  };

  const approveDriver = async (u) => {
    if (approvingId) return;
    if (!window.confirm(`Approve ${u.name} (${u.email})? They'll receive a set-password link (valid 15 minutes) and can start bidding.`)) return;
    setApprovingId(u.user_id);
    try {
      await api.post(`/admin/drivers/${u.user_id}/approve`, { origin_url: window.location.origin });
      toast.success(`${u.email} approved — set-password email sent.`);
      loadPending();
      load(""); // they now appear in the users list
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setApprovingId(null);
    }
  };

  const openReject = (u) => {
    if (approvingId) return;
    setRejectTarget(u);
    setRejectReason("");
  };

  const confirmReject = async () => {
    const reason = rejectReason.trim();
    if (!reason || !rejectTarget || approvingId) return;
    setApprovingId(rejectTarget.user_id);
    try {
      await api.post(`/admin/drivers/${rejectTarget.user_id}/reject`, { reason, origin_url: window.location.origin });
      toast.success(`${rejectTarget.email} rejected — they were emailed the reason with a resubmission link.`);
      setRejectTarget(null);
      loadPending();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setApprovingId(null);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // user in the delete-confirm modal
  const [deleting, setDeleting] = useState(false);
  const [activity, setActivity] = useState([]); // admin activity log (deletions)

  const loadActivity = async () => {
    try {
      const { data } = await api.get("/admin/activity");
      setActivity(data);
    } catch {
      /* activity panel is non-fatal */
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(
        `Deleted ${data.deleted} — confirmation email sent` +
          (data.open_requests_cancelled ? `, ${data.open_requests_cancelled} open request${data.open_requests_cancelled === 1 ? "" : "s"} cancelled.` : ".")
      );
      setUsers((list) => list.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
      loadActivity();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete the account."));
    } finally {
      setDeleting(false);
    }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/admin/role-changes");
      setAudit(data);
    } catch {
      /* audit panel is non-fatal */
    }
  };

  const loadOptouts = async () => {
    try {
      const { data } = await api.get("/admin/sms-optouts");
      setOptouts(data);
    } catch {
      /* opt-out panel is non-fatal */
    }
  };

  useEffect(() => {
    loadAudit();
    loadPending();
    loadOptouts();
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (q) => {
    try {
      const { data } = await api.get("/admin/users", { params: { search: q } });
      setUsers(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Debounced live search by email
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const optOutSet = new Set(optouts.map((o) => o.phone));

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setSaving(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/role`, { role });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${u.email} is now a ${role} — effective immediately.`);
      loadAudit();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  const toggleSuspend = async (u) => {
    const next = !u.suspended;
    if (next && !window.confirm(`Suspend ${u.email}? They are locked out of their account immediately.`)) return;
    setSaving(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/suspension`, { suspended: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(next ? `${u.email} suspended — effective immediately.` : `${u.email} reactivated.`);
      loadAudit();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-5xl mx-auto" data-testid="admin-users-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          User management
        </h1>
        <p className="text-sm text-zinc-500 mt-3" data-testid="admin-users-count">
          {users.length} registered {users.length === 1 ? "user" : "users"} — shippers and drivers. Role changes apply to their account immediately.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email…"
            data-testid="user-search"
            className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2.5 pl-9 pr-3 text-sm"
          />
        </div>
        <button
          onClick={openBroadcast}
          data-testid="sms-broadcast-btn"
          title="Text every registered driver who has a phone number on file"
          className="inline-flex items-center gap-2 border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold transition-colors"
        >
          <Megaphone className="w-3.5 h-3.5" /> Send SMS broadcast
        </button>
        <button
          onClick={exportConsent}
          data-testid="consent-export-btn"
          title="Download TOS + SMS consent records (timestamps and IPs) as CSV"
          className="inline-flex items-center gap-2 border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Consent CSV
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
          <UserCog className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-2xl font-black uppercase">No users found</div>
          <p className="text-zinc-500 text-sm mt-2">Try a different email search.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className="border border-white/[0.08] bg-[#0F0F10] p-5 flex items-center justify-between gap-4 flex-wrap hover:border-[#FF3B30]/40 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg font-black uppercase truncate">{u.name}</div>
                  {u.suspended && (
                    <span
                      data-testid={`user-suspended-badge-${u.id}`}
                      className="text-[10px] uppercase tracking-widest font-black bg-red-500/90 text-white px-2 py-0.5"
                    >
                      Suspended
                    </span>
                  )}
                  <EmailBlockedBadge count={u.email_blocked_count} id={u.id} />
                  {optOutSet.has(normPhone(u.phone)) && (
                    <span
                      data-testid={`sms-optout-badge-${u.id}`}
                      title="Replied STOP to texts — re-engage by phone if needed."
                      className="text-[10px] uppercase tracking-widest font-black border border-zinc-500/60 text-zinc-400 px-2 py-0.5 cursor-help"
                    >
                      SMS opted out
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate" data-testid={`user-email-${u.id}`}>{u.email}</div>
                {(u.phone || "").trim() ? (
                  <div className="text-xs text-zinc-400 truncate mt-0.5" data-testid={`user-phone-${u.id}`}>
                    {fmtPhone(u.phone) || u.phone}
                  </div>
                ) : (
                  <div className="text-xs text-zinc-700 truncate mt-0.5" data-testid={`user-phone-${u.id}`}>No phone</div>
                )}
                {u.created_at && (
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                    Joined {new Date(u.created_at).toLocaleDateString()}
                  </div>
                )}
                {u.tos_accepted_at && (
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5" data-testid={`tos-consent-${u.id}`}>
                    TOS agreed {new Date(u.tos_accepted_at).toLocaleString()}{u.tos_accepted_ip ? ` · IP ${u.tos_accepted_ip}` : ""}
                  </div>
                )}
                {u.sms_opt_in_at && (
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5" data-testid={`sms-consent-${u.id}`}>
                    SMS opted in {new Date(u.sms_opt_in_at).toLocaleString()}{u.sms_opt_in_ip ? ` · IP ${u.sms_opt_in_ip}` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold">Role</span>
                <select
                  value={u.role}
                  disabled={saving === u.id}
                  onChange={(e) => changeRole(u, e.target.value)}
                  data-testid={`user-role-select-${u.id}`}
                  className={`bg-[#141416] border focus:border-[#FF3B30] focus:outline-none text-xs uppercase tracking-widest font-bold px-3 py-2 transition-opacity ${
                    saving === u.id ? "opacity-50 cursor-wait border-white/[0.08]" : "border-white/[0.12] hover:border-white/30 cursor-pointer"
                  } ${u.role === "driver" ? "text-[#FF3B30]" : "text-white"}`}
                >
                  <option value="shipper">Shipper</option>
                  <option value="driver">Driver</option>
                </select>
                <button
                  onClick={() => toggleSuspend(u)}
                  disabled={saving === u.id}
                  data-testid={`user-suspend-${u.id}`}
                  className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-all ${
                    u.suspended
                      ? "border-emerald-500/60 text-emerald-400 hover:bg-emerald-500 hover:text-black"
                      : "border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white"
                  } disabled:opacity-50`}
                >
                  {u.suspended ? "Reactivate" : "Suspend"}
                </button>
                <button
                  onClick={() => setDeleteTarget(u)}
                  data-testid={`user-delete-${u.id}`}
                  title="Permanently delete this account and all its records"
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 border border-red-500/40 text-red-500 hover:bg-red-600 hover:text-white transition-all inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10" data-testid="pending-drivers-section">
        <h2 className="font-display text-2xl font-black uppercase mb-1">Pending drivers</h2>
        <p className="text-sm text-zinc-500 mb-4">
          New registrations with FMCSA-verified DOT numbers. Review their documents, then approve (emails a set-password link, valid 15 minutes) or reject.
        </p>
        {pendingLoading ? (
          <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading pending drivers…</div>
        ) : pending.length === 0 ? (
          <div className="border border-dashed border-white/[0.08] p-8 text-center bg-[#0F0F10]" data-testid="pending-empty">
            <div className="font-display text-xl font-black uppercase">No pending drivers</div>
            <p className="text-zinc-500 text-sm mt-2">New driver applications will show up here for review.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((u) => (
              <div
                key={u.user_id}
                data-testid={`pending-row-${u.user_id}`}
                className="border border-amber-500/30 bg-[#0F0F10] p-4 hover:border-amber-500/60 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-black uppercase truncate">{u.name}</div>
                      <span className="text-[10px] uppercase tracking-widest font-black bg-amber-500/90 text-black px-2 py-0.5">
                        Pending
                      </span>
                      {(() => {
                        const chip = slaChip(u);
                        return chip ? (
                          <span
                            data-testid={`sla-chip-${u.user_id}`}
                            className={`text-[10px] uppercase tracking-widest font-black border px-2 py-0.5 ${chip.cls}`}
                          >
                            Waiting {chip.label}
                          </span>
                        ) : null;
                      })()}
                      <EmailBlockedBadge count={u.email_blocked_count} id={u.user_id} />
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                    {u.dot_number && (
                      <div className="text-[11px] text-zinc-400 mt-1.5">
                        USDOT <span className="text-white font-bold">{u.dot_number}</span>
                        {u.dot_legal_name && <span className="text-zinc-500"> · {u.dot_legal_name}</span>}
                        <span className="text-emerald-400 font-bold"> · FMCSA verified</span>
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                      Applied {new Date(u.created_at).toLocaleDateString()}
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {u.docs.map((d) => (
                        <a
                          key={d.id}
                          href={`${API_BASE}/admin/kyc/${d.id}/file/raw?token=${getToken()}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`pending-doc-${d.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 border border-white/[0.12] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                        >
                          View {d.doc_type}
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openReject(u)}
                      disabled={approvingId === u.user_id}
                      data-testid={`reject-driver-${u.user_id}`}
                      className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => approveDriver(u)}
                      disabled={approvingId === u.user_id}
                      data-testid={`approve-driver-${u.user_id}`}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 bg-emerald-500 text-black hover:bg-emerald-400 transition-colors disabled:opacity-50"
                    >
                      {approvingId === u.user_id ? "Working…" : "Approve"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {audit.length > 0 && (
        <div className="mt-10" data-testid="role-audit-section">
          <h2 className="font-display text-2xl font-black uppercase mb-3">Recent account changes</h2>
          <div className="border border-white/[0.08] bg-[#0F0F10] divide-y divide-white/[0.06]">
            {audit.map((a) => (
              <div
                key={a.id}
                className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`audit-row-${a.id}`}
              >
                <div className="text-sm">
                  <span className="text-white font-semibold">{a.user_email}</span>
                  {a.action ? (
                    <span className={`font-bold uppercase text-[11px] tracking-widest ${a.action === "suspended" ? "text-red-400" : "text-emerald-400"}`}>
                      {" "}{a.action}
                    </span>
                  ) : (
                    <span className="text-zinc-400">
                      {" "}{a.from_role} → <span className="text-[#FF3B30] font-bold">{a.to_role}</span>
                    </span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">
                  by {a.admin_email} · {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activity.length > 0 && (
        <div className="mt-10" data-testid="admin-activity-section">
          <h2 className="font-display text-2xl font-black uppercase mb-1">Admin activity</h2>
          <p className="text-xs text-zinc-500 mb-3">Permanent actions taken by admins — deletions record who did it, when, and whose account it was.</p>
          <div className="border border-white/[0.08] bg-[#0F0F10] divide-y divide-white/[0.06]">
            {activity.map((a) => (
              <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`activity-row-${a.id}`}>
                <div className="text-sm">
                  <span className="text-red-400 font-bold uppercase text-[11px] tracking-widest">deleted</span>{" "}
                  <span className="text-white font-semibold" data-testid={`activity-target-${a.id}`}>{a.target_email}</span>
                  {a.target_role && <span className="text-zinc-500 text-[11px] uppercase tracking-widest"> ({a.target_role})</span>}
                  {a.open_requests_cancelled > 0 && (
                    <span className="text-zinc-500 text-[11px]"> — {a.open_requests_cancelled} open request{a.open_requests_cancelled === 1 ? "" : "s"} cancelled</span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600" data-testid={`activity-meta-${a.id}`}>
                  by {a.admin_name || a.admin_email} · {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10" data-testid="sms-optouts-section">
        <h2 className="font-display text-2xl font-black uppercase mb-1">SMS opt-outs</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Numbers that replied STOP — we can't text them until they reply START. Re-engage by phone if it matters.
        </p>
        {optouts.length === 0 ? (
          <p className="text-sm text-zinc-600 border border-white/[0.08] bg-[#0F0F10] px-4 py-6 text-center" data-testid="sms-optouts-empty">
            Nobody has opted out of texts.
          </p>
        ) : (
          <div className="border border-white/[0.08] bg-[#0F0F10] divide-y divide-white/[0.06]">
            {optouts.map((o) => (
              <div
                key={o.phone}
                className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`sms-optout-row-${o.phone}`}
              >
                <div className="text-sm">
                  <span className="text-white font-semibold">{o.name || "No matching account"}</span>{" "}
                  <span className="text-zinc-500">{o.email || ""}</span>
                </div>
                <div className="text-[11px] text-zinc-500">
                  <span className="font-mono text-zinc-300">{o.phone}</span>
                  <span className="text-zinc-600"> · opted out {new Date(o.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="bg-[#0F0F10] border-white/[0.12] text-white sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black uppercase text-red-400">Delete account?</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              This is permanent — there is no undo.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <>
              <p className="text-sm text-zinc-300 leading-relaxed" data-testid="delete-user-copy">
                You're about to permanently delete <span className="text-white font-bold">{deleteTarget.name}</span>{" "}
                (<span className="text-white">{deleteTarget.email}</span>) — their profile, KYC documents, bids,
                requests, messages, and every related record. Any open requests are cancelled and bidding drivers
                are notified. The user gets a confirmation email, and the deletion is logged in the admin activity log.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  data-testid="delete-user-cancel"
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.12] text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  Keep account
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={deleting}
                  data-testid="delete-user-confirm"
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete permanently"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bcOpen} onOpenChange={(open) => { if (!bcSending) { setBcOpen(open); if (!open) { setBcArmed(false); setBcResult(null); } } }}>
        <DialogContent className="bg-[#0F0F10] border-white/[0.12] text-white sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="sms-broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black uppercase">SMS broadcast</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Texts every registered driver with a phone number on file. <span className="text-zinc-300 font-mono text-xs">[Driver First Name]</span> is personalized per driver. Drivers who texted STOP are always excluded.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mt-1" data-testid="sms-broadcast-audience-picker">
            {[
              ["incomplete", "Haven't finished onboarding"],
              ["all", "All drivers with a phone"],
            ].map(([key, label]) => {
              const stats = bcPreview?.audiences?.[key];
              const active = bcAudience === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setBcAudience(key); setBcArmed(false); setBcResult(null); }}
                  data-testid={`sms-broadcast-audience-${key}`}
                  className={`flex-1 text-left border px-3 py-2.5 transition-colors ${
                    active ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/[0.08] bg-[#141416] hover:border-white/30"
                  }`}
                >
                  <div className={`text-[11px] uppercase tracking-widest font-bold ${active ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                    {label}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {stats ? `${stats.reachable} reachable` : "…"}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="border border-white/[0.08] bg-[#141416] px-4 py-3 mt-2" data-testid="sms-broadcast-preview">
            {bcStats ? (
              <>
                <div className="text-sm">
                  Will text <span className="text-white font-bold">{bcStats.reachable}</span> of{" "}
                  {bcStats.drivers_with_phone} driver{bcStats.drivers_with_phone === 1 ? "" : "s"} with a phone on file
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {bcStats.sms_opted_in} opted in to SMS · {bcStats.stop_opted_out} replied STOP (excluded) · {bcStats.invalid_phone} invalid {bcStats.invalid_phone === 1 ? "number" : "numbers"} (skipped)
                </div>
              </>
            ) : (
              <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Counting recipients…</div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3">
            <select
              value={bcTemplateId}
              onChange={(e) => pickTemplate(e.target.value)}
              data-testid="sms-template-select"
              className="flex-1 min-w-0 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white py-2 px-3 text-sm"
            >
              <option value="">Custom message</option>
              <option value="default">Default: profile-completion nudge</option>
              {bcTemplates.map((t) => (
                <option key={t.id} value={t.id} data-testid={`sms-template-option-${t.id}`}>
                  {t.name}
                </option>
              ))}
            </select>
            {bcTemplateId && bcTemplateId !== "default" && (
              <button
                type="button"
                onClick={deleteTemplate}
                title="Delete this template"
                data-testid="sms-template-delete"
                className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => { setBcTplSaveOpen((v) => !v); setBcTplName(""); }}
              data-testid="sms-template-save-toggle"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-2 border border-white/[0.12] text-zinc-400 hover:border-white hover:text-white transition-colors whitespace-nowrap"
            >
              <Save className="w-3 h-3" /> Save as template
            </button>
          </div>
          {bcTplSaveOpen && (
            <div className="flex items-center gap-2 mt-2" data-testid="sms-template-save-form">
              <input
                value={bcTplName}
                onChange={(e) => setBcTplName(e.target.value)}
                maxLength={80}
                placeholder="Template name — e.g. Friday onboarding nudge"
                data-testid="sms-template-name"
                className="flex-1 min-w-[180px] bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2 px-3 text-sm"
              />
              <button
                type="button"
                onClick={saveTemplate}
                disabled={bcTplSaving || !bcTplName.trim() || !bcMsg.trim()}
                data-testid="sms-template-save-confirm"
                className="text-[10px] uppercase tracking-widest font-bold px-4 py-2 bg-[#FF3B30] text-white hover:bg-[#ff564d] transition-colors disabled:opacity-40 whitespace-nowrap"
              >
                {bcTplSaving ? "Saving…" : "Save"}
              </button>
            </div>
          )}

          <textarea
            value={bcMsg}
            onChange={(e) => { setBcMsg(e.target.value); setBcArmed(false); setBcResult(null); }}
            rows={6}
            maxLength={1000}
            data-testid="sms-broadcast-message"
            className="mt-3 w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3 text-sm resize-none"
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1" data-testid="sms-broadcast-charcount">
            {bcMsg.length}/1000 chars · ~{smsSegments(bcMsg.length)} SMS segment{smsSegments(bcMsg.length) === 1 ? "" : "s"} per driver
          </div>

          <div className="flex items-center gap-2 mt-3">
            <input
              value={bcTestPhone}
              onChange={(e) => setBcTestPhone(e.target.value)}
              placeholder="Your phone — (555) 123-4567"
              data-testid="sms-broadcast-test-phone"
              className="flex-1 min-w-[180px] bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2 px-3 text-sm"
            />
            <button
              type="button"
              onClick={sendBroadcastTest}
              disabled={bcTestSending || !bcTestPhone.trim() || !bcMsg.trim() || bcSending}
              data-testid="sms-broadcast-test-send"
              className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 border border-white/[0.12] text-zinc-400 hover:border-white hover:text-white transition-colors disabled:opacity-40 whitespace-nowrap"
            >
              {bcTestSending ? "Sending…" : "Send test to me"}
            </button>
          </div>

          <div className="border border-white/[0.08] bg-[#141416] px-4 py-3 mt-3" data-testid="sms-broadcast-history">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
              Past broadcasts — delivery rate
            </div>
            {bcHistory === null ? (
              <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading history…</div>
            ) : bcHistory.length === 0 ? (
              <p className="text-[11px] text-zinc-600" data-testid="sms-broadcast-history-empty">
                No broadcasts yet — delivery stats show up here after your first blast.
              </p>
            ) : (
              <div className="space-y-1.5">
                {bcHistory.slice(0, 6).map((h) => {
                  const pct = h.delivery_rate === null || h.delivery_rate === undefined ? null : Math.round(h.delivery_rate * 100);
                  const tone = pct === null ? "text-zinc-500" : pct >= 90 ? "text-emerald-400" : pct >= 70 ? "text-amber-400" : "text-red-400";
                  const bar = pct === null ? "bg-zinc-700" : pct >= 90 ? "bg-emerald-400" : pct >= 70 ? "bg-amber-400" : "bg-red-400";
                  return (
                    <div key={h.id} data-testid={`sms-broadcast-history-row-${h.id}`} className="flex items-center gap-3 text-[11px]">
                      <span className="text-zinc-500 whitespace-nowrap w-16">{new Date(h.created_at).toLocaleDateString()}</span>
                      <span className="text-zinc-500 uppercase tracking-widest text-[9px] w-20 truncate" title={h.body}>
                        {h.audience === "incomplete" ? "Onboarding" : "All drivers"}
                      </span>
                      <div className="flex-1 h-1.5 bg-white/[0.06]">
                        <div className={`h-1.5 ${bar}`} style={{ width: `${pct ?? 0}%` }} />
                      </div>
                      <span className={`font-bold ${tone} w-10 text-right`} data-testid={`sms-broadcast-history-rate-${h.id}`}>
                        {pct === null ? "—" : `${pct}%`}
                      </span>
                      <span className="text-zinc-600 whitespace-nowrap">{h.recipient_count} rcpt</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {bcResult && (
            <div className="border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 mt-3" data-testid="sms-broadcast-result">
              <div className="text-sm text-emerald-300 font-bold uppercase tracking-widest">
                Broadcast complete — {bcResult.sent} sent · {bcResult.failed} failed · {bcResult.skipped} skipped
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">
                Logged with timestamp, message, and recipient count ({bcResult.recipient_count}) in the{" "}
                <a href="/app/admin/email" className="text-[#FF3B30] underline">Email / SMS log</a>.
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => setBcOpen(false)}
              disabled={bcSending}
              data-testid="sms-broadcast-cancel"
              className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.12] text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={sendBroadcast}
              disabled={bcSending || !bcMsg.trim() || (bcStats && bcStats.reachable === 0)}
              data-testid="sms-broadcast-send"
              className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 transition-colors disabled:opacity-40 ${
                bcArmed ? "bg-red-500 text-white hover:bg-red-400" : "bg-[#FF3B30] text-white hover:bg-[#ff564d]"
              }`}
            >
              {bcSending
                ? "Sending…"
                : bcArmed
                ? `Confirm — text ${bcStats ? bcStats.reachable : ""} driver${bcStats && bcStats.reachable === 1 ? "" : "s"} now`
                : "Send broadcast"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open && !approvingId) setRejectTarget(null); }}>
        <DialogContent className="bg-[#0F0F10] border-white/[0.12] text-white sm:max-w-lg" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black uppercase">
              Reject {rejectTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {rejectTarget?.email} sees this reason in the decision email, along with a link to resubmit fixed documents.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="reject-presets">
            {REJECT_PRESETS.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setRejectReason(p)}
                data-testid={`reject-preset-${i}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${
                  rejectReason === p
                    ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]"
                    : "border-white/[0.12] text-zinc-400 hover:border-white/40 hover:text-white"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Or write a specific reason…"
            data-testid="reject-reason-input"
            className="mt-3 w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3 text-sm resize-none"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => setRejectTarget(null)}
              disabled={!!approvingId}
              data-testid="reject-cancel"
              className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.12] text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReject}
              disabled={!rejectReason.trim() || !!approvingId}
              data-testid="reject-confirm"
              className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 bg-red-500 text-white hover:bg-red-400 transition-colors disabled:opacity-40"
            >
              {approvingId ? "Working…" : "Send rejection"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
